import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { parcelAPI } from '../../services/api';
import { format } from 'date-fns';

const fmtDate = (d, fmt = 'yyyy/MM/dd HH:mm') => (d ? format(new Date(d), fmt) : '—');
const fmt = (n) => Number(n || 0).toLocaleString('ar-SD', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const openReceipt = async (rawUrl) => {
  if (!rawUrl) return;
  const url = rawUrl.replace(/\/uploads\/(receipt-[^/?#]+)/, '/api/receipts/$1');
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('server error');
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
  } catch {
    toast.error('تعذّر فتح الإيصال');
  }
};

const StatCard = ({ label, value, accent }) => (
  <div className="bg-white rounded-2xl border p-4">
    <p className="text-xs text-gray-500 font-semibold">{label}</p>
    <p className={`text-2xl font-black mt-1 tabular-nums ${accent || 'text-gray-900'}`}>{value}</p>
  </div>
);

const PRICING_FIELDS = [
  { key: 'baseFee', label: 'رسوم أساسية ثابتة', hint: 'تُضاف لكل طرد (ج.س)' },
  { key: 'freeKg', label: 'الوزن المجاني', hint: 'أول كيلوغرامات ضمن الرسوم الأساسية (كجم)' },
  { key: 'perKgRate', label: 'سعر الكيلوغرام الإضافي', hint: 'لكل كيلوغرام يتجاوز الوزن المجاني (ج.س)' },
  { key: 'insurancePercent', label: 'نسبة التأمين', hint: 'من القيمة المصرّح بها عند طلب التأمين (%)' },
  { key: 'fragileFee', label: 'رسوم قابل للكسر', hint: 'رسوم إضافية ثابتة عند تحديد "قابل للكسر" (ج.س)' },
  { key: 'transportPercentOfFare', label: 'نسبة رسوم النقل', hint: 'من سعر تذكرة الرحلة المرتبطة (%)' },
];

const PricingSettings = () => {
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await parcelAPI.getPricingRules();
      setRules(res.data.data);
      setForm(res.data.data);
    } catch { toast.error('خطأ في تحميل إعدادات أسعار الطرود'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await parcelAPI.updatePricingRules(form);
      setRules(res.data.data);
      setForm(res.data.data);
      toast.success('تم حفظ أسعار الطرود');
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في حفظ الأسعار');
    } finally { setSaving(false); }
  };

  const tiers = form.weightTiers || [];
  const setTiers = (next) => setForm((s) => ({ ...s, weightTiers: next }));
  const addTier = () => setTiers([...tiers, { maxKg: '', price: '' }]);
  const removeTier = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  const updateTier = (i, field, value) => setTiers(tiers.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));
  const toggleManualLastTier = (checked) => {
    if (tiers.length === 0) return;
    const next = [...tiers];
    next[next.length - 1] = checked ? { maxKg: null, price: null } : { maxKg: '', price: '' };
    setTiers(next);
  };
  const lastTierIsManual = tiers.length > 0 && tiers[tiers.length - 1].maxKg === null;

  const dirty = rules && (
    PRICING_FIELDS.some((f) => String(form[f.key] ?? '') !== String(rules[f.key] ?? ''))
    || JSON.stringify(form.weightTiers || []) !== JSON.stringify(rules.weightTiers || [])
  );

  return (
    <div className="bg-white rounded-2xl border overflow-hidden mb-5">
      <div className="px-4 py-3 border-b bg-gray-50">
        <h2 className="text-sm font-bold text-gray-700">أسعار خدمة الطرود</h2>
        <p className="text-xs text-gray-500 mt-0.5">تخصيص أسعار حساب طرودكم — أي حقل تتركه فارغاً يستخدم القيمة الافتراضية</p>
      </div>
      {loading ? (
        <div className="py-10 text-center text-gray-400">جاري التحميل...</div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRICING_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-bold text-gray-600 mb-1">{f.label}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form[f.key] ?? ''}
                  onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 tabular-nums"
                />
                <p className="text-[11px] text-gray-400 mt-1">{f.hint}</p>
              </div>
            ))}
          </div>

          {/* Tiered weight-bracket pricing — optional, overrides the fixed
              fee + per-kg rate above whenever the parcel's weight matches a
              configured bracket. Closer to how a route tariff sheet reads. */}
          <div className="mt-6 pt-5 border-t">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-bold text-gray-700">جدول أسعار حسب فئة الوزن (اختياري)</h3>
              <button onClick={addTier} className="text-xs font-bold text-blue-700 hover:text-blue-900">+ إضافة فئة</button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              إن أُضيفت فئات هنا، يُستخدم السعر الثابت للفئة المطابقة بدل الرسوم الأساسية وسعر الكيلوغرام أعلاه.
              اتركها فارغة للاستمرار بالحساب حسب الكيلوغرام.
            </p>
            {tiers.length === 0 ? (
              <p className="text-xs text-gray-400 italic">لا توجد فئات — الحساب حالياً حسب الكيلوغرام</p>
            ) : (
              <div className="space-y-2">
                {tiers.map((t, i) => {
                  const isLast = i === tiers.length - 1;
                  const isManual = isLast && t.maxKg === null;
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-16 flex-shrink-0">حتى وزن</span>
                      {isManual ? (
                        <span className="flex-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                          وما فوق — تسعير يدوي من المكتب
                        </span>
                      ) : (
                        <>
                          <input type="number" min="0.1" step="0.1" placeholder="كجم" value={t.maxKg ?? ''}
                            onChange={(e) => updateTier(i, 'maxKg', e.target.value)}
                            className="w-24 border rounded-xl px-2 py-2 text-sm tabular-nums" />
                          <span className="text-xs text-gray-500">السعر</span>
                          <input type="number" min="0" step="1" placeholder="ج.س" value={t.price ?? ''}
                            onChange={(e) => updateTier(i, 'price', e.target.value)}
                            className="flex-1 border rounded-xl px-3 py-2 text-sm tabular-nums" />
                        </>
                      )}
                      <button onClick={() => removeTier(i)} className="text-red-500 hover:text-red-700 text-lg leading-none px-1">×</button>
                    </div>
                  );
                })}
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 pt-1">
                  <input type="checkbox" checked={lastTierIsManual} onChange={(e) => toggleManualLastTier(e.target.checked)} />
                  الأوزان الأكبر من آخر فئة تتطلب تسعيراً يدوياً (بدلاً من الاستمرار بسعر الكيلوغرام)
                </label>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-5">
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white text-sm font-bold px-5 py-2.5 rounded-xl"
            >
              {saving ? '...جارٍ الحفظ' : 'حفظ الأسعار'}
            </button>
            {dirty && <span className="text-xs text-amber-600 font-semibold">هناك تغييرات غير محفوظة</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const INCIDENT_TYPE_LABELS = {
  damaged: { label: 'تلف', color: 'bg-red-100 text-red-700' },
  lost: { label: 'مفقود', color: 'bg-red-100 text-red-700' },
  delayed: { label: 'تأخير', color: 'bg-amber-100 text-amber-700' },
  other: { label: 'أخرى', color: 'bg-gray-100 text-gray-600' },
};

const IncidentsPanel = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [resolution, setResolution] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await parcelAPI.getIncidents(onlyOpen ? 'open' : undefined);
      setIncidents(res.data.data || []);
    } catch { toast.error('خطأ في تحميل الحوادث'); }
    finally { setLoading(false); }
  }, [onlyOpen]);

  useEffect(() => { load(); }, [load]);

  const submitResolve = async () => {
    if (!resolving) return;
    if (!resolution.trim()) return toast.error('نص الحل مطلوب');
    setSaving(true);
    try {
      await parcelAPI.resolveIncident(resolving.id, { resolution: resolution.trim() });
      toast.success('تم تحديث الحادثة');
      setResolving(null); setResolution('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في تحديث الحادثة');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border overflow-hidden mb-5">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-bold text-gray-700">حوادث الطرود (تلف / فقدان / تأخير)</h2>
          <p className="text-xs text-gray-500 mt-0.5">بلاغات المكاتب — تحتاج قراراً وتواصلاً مع العميل</p>
        </div>
        <button onClick={() => setOnlyOpen((v) => !v)} className="text-xs font-semibold text-blue-700 hover:underline">
          {onlyOpen ? 'عرض الكل' : 'عرض المفتوحة فقط'}
        </button>
      </div>
      {loading ? (
        <div className="py-10 text-center text-gray-400">جاري التحميل...</div>
      ) : incidents.length === 0 ? (
        <div className="py-10 text-center text-gray-400">لا توجد حوادث{onlyOpen ? ' مفتوحة' : ''}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">الطرد</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">النوع</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">الوصف</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">المكتب</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">الحالة</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">إجراء</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {incidents.map((inc) => {
                const t = INCIDENT_TYPE_LABELS[inc.type] || INCIDENT_TYPE_LABELS.other;
                return (
                  <tr key={inc.id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3">
                      <span className="font-mono font-bold text-blue-700">{inc.parcel?.trackingNumber}</span>
                      <br /><span className="text-xs text-gray-400">{inc.parcel?.senderName} ← {inc.parcel?.receiverName}</span>
                    </td>
                    <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${t.color}`}>{t.label}</span></td>
                    <td className="px-4 py-3 text-gray-700 text-xs max-w-xs">
                      {inc.description}
                      {inc.photoUrls?.length > 0 && (
                        <div className="flex gap-1 mt-1.5">
                          {inc.photoUrls.map((u, i) => (
                            <button key={i} onClick={() => openReceipt(u)} className="text-blue-600 hover:underline text-[11px] font-semibold">صورة {i + 1}</button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{inc.office?.name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        inc.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : inc.status === 'investigating' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {inc.status === 'resolved' ? 'محلولة' : inc.status === 'investigating' ? 'قيد المتابعة' : 'مفتوحة'}
                      </span>
                      {inc.resolution && <p className="text-[11px] text-gray-500 mt-1 max-w-[160px]">{inc.resolution}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {inc.status !== 'resolved' && (
                        <button onClick={() => { setResolving(inc); setResolution(''); }} className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg">حل الحادثة</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {resolving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-lg mb-1">حل الحادثة</h3>
            <p className="text-xs text-gray-500 mb-4">#{resolving.parcel?.trackingNumber} — {INCIDENT_TYPE_LABELS[resolving.type]?.label}</p>
            <label className="block text-xs font-bold text-gray-600 mb-1">القرار / الحل *</label>
            <textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)}
              placeholder="مثال: تم تعويض العميل بمبلغ ٥٠٠ ج.س، أو: تم العثور على الطرد وإعادة شحنه"
              className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <div className="flex gap-2 mt-5">
              <button onClick={submitResolve} disabled={saving} className="flex-1 bg-blue-700 hover:bg-blue-800 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60">
                {saving ? '...جارٍ الحفظ' : 'حفظ الحل'}
              </button>
              <button onClick={() => setResolving(null)} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function CompanyParcelsPage() {
  const [pending, setPending] = useState([]);
  const [lateDropoffs, setLateDropoffs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null); // { parcel, mode: 'approve'|'reject' }
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [grantingException, setGrantingException] = useState(null);
  const [exceptionReason, setExceptionReason] = useState('');
  const [savingException, setSavingException] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, statsRes, lateRes] = await Promise.all([
        parcelAPI.getPending(), parcelAPI.getStats(), parcelAPI.getLateDropoffs(),
      ]);
      setPending(pendingRes.data.data || []);
      setStats(statsRes.data.data);
      setLateDropoffs(lateRes.data.data || []);
    } catch { toast.error('خطأ في تحميل بيانات الطرود'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submitReview = async () => {
    if (!reviewing) return;
    setSaving(true);
    try {
      if (reviewing.mode === 'approve') {
        await parcelAPI.confirmPayment(reviewing.parcel.id, { comments: comment });
        toast.success('تم تأكيد الدفع');
      } else {
        await parcelAPI.rejectPayment(reviewing.parcel.id, { reason: comment });
        toast.success('تم رفض الدفع');
      }
      setReviewing(null); setComment('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في تنفيذ الإجراء');
    } finally { setSaving(false); }
  };

  const submitException = async () => {
    if (!grantingException || !exceptionReason.trim()) return toast.error('سبب التجاوز الاستثنائي مطلوب');
    setSavingException(true);
    try {
      await parcelAPI.approveLateException(grantingException.id, { reason: exceptionReason.trim() });
      toast.success('تم اعتماد التجاوز الاستثنائي');
      setGrantingException(null); setExceptionReason('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في اعتماد التجاوز');
    } finally { setSavingException(false); }
  };

  return (
    <div dir="rtl" className="p-4 md:p-6">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900">إدارة الطرود</h1>
        <p className="text-xs text-gray-500 mt-0.5">مراجعة الدفعات، إحصائيات، وإيرادات خدمة الطرود</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <StatCard label="إجمالي الطرود" value={stats.total} />
          <StatCard label="الإيرادات" value={`${fmt(stats.revenue)} ج.س`} accent="text-emerald-600" />
          <StatCard label="بانتظار المراجعة" value={stats.byStatus?.payment_submitted || 0} accent="text-amber-600" />
          <StatCard label="مكتملة" value={stats.byStatus?.completed || 0} accent="text-blue-600" />
        </div>
      )}

      <PricingSettings />

      <IncidentsPanel />

      <div className="bg-white rounded-2xl border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h2 className="text-sm font-bold text-gray-700">طرود بانتظار مراجعة الدفع ({pending.length})</h2>
        </div>
        {loading ? (
          <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
        ) : pending.length === 0 ? (
          <div className="py-16 text-center text-gray-400">لا توجد طرود بانتظار المراجعة</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">رقم التتبع</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">المرسل</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">الرحلة</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">المبلغ</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">الإيصال</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">إجراء</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pending.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-bold text-blue-700">{p.trackingNumber}</td>
                    <td className="px-4 py-3 text-gray-700">{p.senderName}<br /><span className="text-xs text-gray-400" dir="ltr">{p.senderPhone}</span></td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {p.trip ? <>{p.trip.fromCity} ← {p.trip.toCity}<br /><span className="text-gray-400">{fmtDate(p.trip.departureTime)}</span></> : '—'}
                    </td>
                    <td className="px-4 py-3 font-semibold tabular-nums">{fmt(p.totalPrice)} ج.س</td>
                    <td className="px-4 py-3">
                      {p.paymentProofUrl ? (
                        <button onClick={() => openReceipt(p.paymentProofUrl)} className="text-blue-600 hover:underline text-xs font-semibold">عرض الإيصال</button>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        <button onClick={() => { setReviewing({ parcel: p, mode: 'approve' }); setComment(''); }} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg">قبول</button>
                        <button onClick={() => { setReviewing({ parcel: p, mode: 'reject' }); setComment(''); }} className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg">رفض</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {lateDropoffs.length > 0 && (
        <div className="bg-white rounded-2xl border overflow-hidden mt-5">
          <div className="px-4 py-3 border-b bg-red-50">
            <h2 className="text-sm font-bold text-red-700">⏰ طرود تجاوزت موعد التسليم ({lateDropoffs.length})</h2>
            <p className="text-xs text-red-600 mt-0.5">انتهت مهلة الـ30 دقيقة قبل الانطلاق — تتطلب اعتماد تجاوز استثنائي قبل أن يتمكن المكتب من استلامها</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">رقم التتبع</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">المرسل</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">الرحلة</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">موعد التسليم الأخير</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">إجراء</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lateDropoffs.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-bold text-blue-700">{p.trackingNumber}</td>
                    <td className="px-4 py-3 text-gray-700">{p.senderName}<br /><span className="text-xs text-gray-400" dir="ltr">{p.senderPhone}</span></td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {p.trip ? <>{p.trip.fromCity} ← {p.trip.toCity}<br /><span className="text-gray-400">{fmtDate(p.trip.departureTime)}</span></> : '—'}
                    </td>
                    <td className="px-4 py-3 text-red-700 text-xs font-semibold">{fmtDate(p.dropoffDeadline)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => { setGrantingException(p); setExceptionReason(''); }} className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
                        اعتماد تجاوز استثنائي
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {grantingException && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-lg mb-1">تجاوز استثنائي لموعد التسليم</h3>
            <p className="text-xs text-gray-500 mb-4">#{grantingException.trackingNumber}</p>
            <label className="block text-xs font-bold text-gray-600 mb-1">سبب التجاوز *</label>
            <textarea
              value={exceptionReason}
              onChange={(e) => setExceptionReason(e.target.value)}
              rows={3}
              className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="مثال: تمت الموافقة هاتفياً مع العميل..."
            />
            <div className="flex gap-2 mt-5">
              <button onClick={submitException} disabled={savingException} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60">
                {savingException ? '...جارٍ الحفظ' : 'اعتماد التجاوز'}
              </button>
              <button onClick={() => setGrantingException(null)} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
            </div>
          </div>
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-lg mb-1">{reviewing.mode === 'approve' ? 'تأكيد دفع الطرد' : 'رفض دفع الطرد'}</h3>
            <p className="text-xs text-gray-500 mb-4">#{reviewing.parcel.trackingNumber}</p>
            <label className="block text-xs font-bold text-gray-600 mb-1">
              {reviewing.mode === 'approve' ? 'ملاحظات (اختياري)' : 'سبب الرفض'}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder={reviewing.mode === 'approve' ? 'أي ملاحظات إضافية...' : 'وضّح سبب رفض الإيصال...'}
            />
            <div className="flex gap-2 mt-5">
              <button
                onClick={submitReview}
                disabled={saving || (reviewing.mode === 'reject' && !comment.trim())}
                className={`flex-1 rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-60 ${reviewing.mode === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {saving ? '...جارٍ الحفظ' : reviewing.mode === 'approve' ? 'تأكيد القبول' : 'تأكيد الرفض'}
              </button>
              <button onClick={() => setReviewing(null)} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
